import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr, fdt } from "../../api";
import { Send, Eye, MailOpen, Inbox as InboxIcon, Megaphone } from "lucide-react";

const inputCls = "cr-input !text-xs !py-2.5";
const btnBlack = "bg-[#1C1B1A] text-white text-xs font-semibold px-4 py-2 rounded-full hover:bg-[#2C2B2A] transition-all disabled:opacity-40";
const btnOutline = "border border-[#E8E4D9] text-[#1C1B1A] text-xs font-semibold px-4 py-2 rounded-full hover:bg-[#F6F3EA] transition-all disabled:opacity-40";

export function MarketingTab() {
  const [segments, setSegments] = useState(null);
  const [form, setForm] = useState({ audience: "courier", subject: "", heading: "", body: "", cta_text: "", cta_url: "" });
  const [preview, setPreview] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [inbox, setInbox] = useState({ items: [], configured: false, address: "" });
  const [reading, setReading] = useState(null);
  const [busy, setBusy] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = useCallback(() => {
    api.get("/admin/marketing/segments").then((r) => setSegments(r.data)).catch(() => {});
    api.get("/admin/marketing/campaigns").then((r) => setCampaigns(r.data.campaigns)).catch(() => {});
    api.get("/admin/inbox").then((r) => setInbox(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const valid = form.subject.trim().length >= 3 && form.heading.trim().length >= 3 && form.body.trim().length >= 10;

  const doPreview = async () => {
    if (!valid) return toast.error("Fill subject, heading and body first");
    try { const { data } = await api.post("/admin/marketing/preview", form); setPreview(data.html); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const doTest = async () => {
    if (!valid) return toast.error("Fill subject, heading and body first");
    setBusy("test");
    try { const { data } = await api.post("/admin/marketing/test", form); toast.success(`Test email sent to ${data.to}`); }
    catch (e) { toast.error(fmtErr(e)); }
    setBusy("");
  };

  const doSend = async () => {
    if (!valid) return toast.error("Fill subject, heading and body first");
    const n = segments?.[form.audience] ?? 0;
    if (!window.confirm(`Send this campaign to ${n} ${form.audience === "all" ? "users" : form.audience === "company" ? "shippers (companies)" : "carriers (couriers)"}?`)) return;
    setBusy("send");
    try {
      const { data } = await api.post("/admin/marketing/campaigns", form);
      toast.success(`Campaign started — ${data.recipients} recipients`);
      setForm({ ...form, subject: "", heading: "", body: "", cta_text: "", cta_url: "" });
      setPreview("");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy("");
  };

  const openMessage = async (m) => {
    try { const { data } = await api.get(`/admin/inbox/${m.id}`); setReading(data); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const audienceLabel = { courier: "Carriers (couriers)", company: "Shippers (companies)", all: "Everyone" };

  return (
    <div className="space-y-5" data-testid="marketing-tab">
      <div className="grid lg:grid-cols-2 gap-5 items-start">
        <section className="cr-card !p-5 space-y-3">
          <h2 className="text-sm font-bold flex items-center gap-2"><Megaphone className="w-4 h-4" /> New campaign
            <span className="text-[10px] font-normal text-[#A09D95]">from office@courierradar.com — replies land in the inbox below</span></h2>
          <div className="inline-flex bg-[#EFECE2] p-1 rounded-full gap-1">
            {["courier", "company", "all"].map((a) => (
              <button key={a} data-testid={`cmp-audience-${a}`} onClick={() => setForm({ ...form, audience: a })}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${form.audience === a ? "bg-[#1C1B1A] text-white" : "text-[#78756E]"}`}>
                {audienceLabel[a]}{segments ? ` (${segments[a]})` : ""}
              </button>
            ))}
          </div>
          <input data-testid="cmp-subject" placeholder="Subject line" value={form.subject} onChange={set("subject")} className={`${inputCls} w-full`} />
          <input data-testid="cmp-heading" placeholder="Email heading (big title inside)" value={form.heading} onChange={set("heading")} className={`${inputCls} w-full`} />
          <textarea data-testid="cmp-body" rows={7} placeholder="Write like a human. Blank line = new paragraph. Tell them what's new, why it helps them, and what to do next…"
            value={form.body} onChange={set("body")} className={`${inputCls} w-full`} />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="cmp-cta-text" placeholder="Button text (optional)" value={form.cta_text} onChange={set("cta_text")} className={inputCls} />
            <input data-testid="cmp-cta-url" placeholder="https://… button link" value={form.cta_url} onChange={set("cta_url")} className={inputCls} />
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <button data-testid="cmp-preview-btn" onClick={doPreview} className={`${btnOutline} flex items-center gap-1.5`}><Eye className="w-3.5 h-3.5" /> Preview</button>
            <button data-testid="cmp-test-btn" onClick={doTest} disabled={busy === "test"} className={`${btnOutline} flex items-center gap-1.5`}><MailOpen className="w-3.5 h-3.5" /> {busy === "test" ? "Sending…" : "Test to myself"}</button>
            <button data-testid="cmp-send-btn" onClick={doSend} disabled={busy === "send"} className={`${btnBlack} flex items-center gap-1.5`}><Send className="w-3.5 h-3.5" /> {busy === "send" ? "Sending…" : "Send campaign"}</button>
          </div>
        </section>
        <section className="cr-card !p-3 bg-[#F6F3EA]" data-testid="cmp-preview-wrap">
          {preview
            ? <iframe title="preview" data-testid="cmp-preview-frame" sandbox="" srcDoc={preview} className="w-full h-[520px] rounded-[20px] border border-[#E8E4D9] bg-white" />
            : <p className="text-xs text-[#A09D95] text-center py-40">Write your campaign and hit Preview — it renders exactly as the email will look.</p>}
        </section>
      </div>

      <section className="cr-card !p-0 cr-table-wrap" data-testid="campaign-history">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[#A09D95] border-b border-[#E8E4D9]">
            <th className="p-3 font-semibold">Sent</th><th className="p-3 font-semibold">Subject</th>
            <th className="p-3 font-semibold">Audience</th><th className="p-3 font-semibold">Delivered</th>
            <th className="p-3 font-semibold">Failed</th><th className="p-3 font-semibold">Status</th>
          </tr></thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id} className="border-b border-[#F6F3EA] last:border-0" data-testid={`campaign-${c.id}`}>
                <td className="p-3 text-[#78756E] whitespace-nowrap">{fdt(c.created_at)}</td>
                <td className="p-3 font-medium">{c.subject}</td>
                <td className="p-3">{audienceLabel[c.audience] || c.audience}</td>
                <td className="p-3 font-semibold">{c.sent}/{c.recipients}</td>
                <td className="p-3">{c.failed}</td>
                <td className="p-3"><span className={c.status === "done" ? "cr-pill-yellow" : "cr-pill-outline"}>{c.status}</span></td>
              </tr>
            ))}
            {campaigns.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#A09D95]">No campaigns yet — your first one is a textarea away.</td></tr>}
          </tbody>
        </table>
      </section>

      <section className="cr-card !p-5" data-testid="owner-inbox">
        <h2 className="text-sm font-bold flex items-center gap-2 mb-1"><InboxIcon className="w-4 h-4" /> Inbox — {inbox.address || "office@courierradar.com"}</h2>
        {!inbox.configured && (
          <p className="text-xs text-[#78756E] bg-[#F6F3EA] rounded-2xl p-3.5 mb-3" data-testid="inbox-setup-hint">
            Receiving isn't connected yet. To read mail sent to office@courierradar.com here, add MX records for Resend receiving on the domain and set RESEND_API_KEY + RESEND_WEBHOOK_SECRET — replies to your campaigns already go to that address.
          </p>
        )}
        {inbox.items.length === 0 && inbox.configured && <p className="text-xs text-[#A09D95] py-6 text-center">Inbox is empty.</p>}
        {inbox.items.map((m) => (
          <button key={m.id} data-testid={`inbox-msg-${m.id}`} onClick={() => openMessage(m)}
            className={`w-full text-left px-3 py-2.5 border-b border-[#F6F3EA] last:border-0 hover:bg-[#FCFAF5] transition-colors ${!m.read ? "bg-[#F6F3EA]" : ""}`}>
            <p className="text-xs font-semibold flex justify-between gap-3"><span className="truncate">{m.from}</span><span className="text-[10px] text-[#A09D95] font-normal shrink-0">{fdt(m.created_at)}</span></p>
            <p className="text-xs text-[#78756E] truncate mt-0.5">{m.subject || "(no subject)"}</p>
          </button>
        ))}
        {reading && (
          <div className="mt-3 border border-[#E8E4D9] rounded-2xl p-4" data-testid="inbox-reading">
            <p className="text-xs font-bold">{reading.subject}</p>
            <p className="text-[10px] text-[#A09D95] mb-2">From {reading.from} · {fdt(reading.created_at)}</p>
            <p className="text-xs text-[#3f3f46] whitespace-pre-wrap leading-relaxed">{reading.text || "(empty message)"}</p>
          </div>
        )}
      </section>

      <FlowsSection />
    </div>
  );
}

function FlowsSection() {
  const [flows, setFlows] = useState(null);
  const [open, setOpen] = useState("");
  const [drafts, setDrafts] = useState({});
  const load = useCallback(() => {
    api.get("/admin/flows").then((r) => {
      setFlows(r.data.flows);
      setDrafts(Object.fromEntries(r.data.flows.map((f) => [f.key, { enabled: f.enabled, steps: f.steps }])));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (key) => {
    try { await api.put(`/admin/flows/${key}`, drafts[key]); toast.success("Flow saved"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const AUDIENCE = { courier: "Carriers", company: "Shippers" };
  return (
    <section className="cr-card !p-5" data-testid="flows-section">
      <h2 className="text-sm font-bold mb-1">Automated engagement flows</h2>
      <p className="text-[11px] text-[#A09D95] mb-4">Onboarding drips and nudges that run on their own. Toggle, rewrite, retime — changes apply to future sends only.</p>
      {(flows || []).map((f) => {
        const draft = drafts[f.key] || { enabled: f.enabled, steps: f.steps };
        const setDraft = (d) => setDrafts({ ...drafts, [f.key]: d });
        return (
          <div key={f.key} className="border border-[#F0EDE4] rounded-2xl p-4 mb-3" data-testid={`flow-${f.key}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-xs font-bold">{f.name} <span className="cr-pill-outline ml-1">{AUDIENCE[f.audience] || f.audience}</span></p>
                <p className="text-[10px] text-[#A09D95] mt-0.5">{f.steps.length} emails · {f.enrolled} enrolled · {f.completed} completed{f.condition ? ` · skips when condition resolved (${f.condition})` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`flow-toggle-${f.key}`} onClick={() => { setDraft({ ...draft, enabled: !draft.enabled }); }}
                  className={draft.enabled ? "cr-pill-dark" : "cr-pill-outline"}>{draft.enabled ? "On" : "Off"}</button>
                <button data-testid={`flow-expand-${f.key}`} onClick={() => setOpen(open === f.key ? "" : f.key)}
                  className={`${btnOutline}`}>{open === f.key ? "Close" : "Edit"}</button>
                <button data-testid={`flow-save-${f.key}`} onClick={() => save(f.key)} className={btnBlack}>Save</button>
              </div>
            </div>
            {open === f.key && (
              <div className="mt-3 space-y-3">
                {draft.steps.map((s, i) => (
                  <div key={i} className="bg-[#FCFAF5] rounded-2xl p-3.5 space-y-2" data-testid={`flow-step-${f.key}-${i}`}>
                    <label className="text-[11px] text-[#78756E] flex items-center gap-2">Send on day
                      <input type="number" min="0" value={s.day} className={`${inputCls} !w-20`}
                        onChange={(e) => { const steps = [...draft.steps]; steps[i] = { ...s, day: parseInt(e.target.value) || 0 }; setDraft({ ...draft, steps }); }} />
                      after approval</label>
                    <input value={s.subject} placeholder="Subject" className={`${inputCls} w-full`} data-testid={`flow-subject-${f.key}-${i}`}
                      onChange={(e) => { const steps = [...draft.steps]; steps[i] = { ...s, subject: e.target.value }; setDraft({ ...draft, steps }); }} />
                    <input value={s.heading} placeholder="Heading" className={`${inputCls} w-full`}
                      onChange={(e) => { const steps = [...draft.steps]; steps[i] = { ...s, heading: e.target.value }; setDraft({ ...draft, steps }); }} />
                    <textarea rows={3} value={s.body} className={`${inputCls} w-full`} data-testid={`flow-body-${f.key}-${i}`}
                      onChange={(e) => { const steps = [...draft.steps]; steps[i] = { ...s, body: e.target.value }; setDraft({ ...draft, steps }); }} />
                    <div className="grid grid-cols-2 gap-2">
                      <input value={s.cta_text || ""} placeholder="Button text" className={inputCls}
                        onChange={(e) => { const steps = [...draft.steps]; steps[i] = { ...s, cta_text: e.target.value }; setDraft({ ...draft, steps }); }} />
                      <input value={s.cta_url || ""} placeholder="/courier or https://…" className={inputCls}
                        onChange={(e) => { const steps = [...draft.steps]; steps[i] = { ...s, cta_url: e.target.value }; setDraft({ ...draft, steps }); }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {flows === null && <p className="text-xs text-[#A09D95]">Loading…</p>}
    </section>
  );
}
